import React from 'react'
import { inject, observer } from 'mobx-react'
import { CreateProduct, CreateChildProduct } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { Button, Table, message, Dropdown, Icon, Menu, Checkbox, Empty } from 'antd'
import {
  API,
  IBaseProduct,
  IInventoryWithLocationName,
  ILocation,
  InventorySearchFilter,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { getPopupContainer, parseErrorMsg } from '../../../../../utilities/helpers'
import { QueryFilter } from '../../../DashboardInventory/View/QueryFilter'
import { browserHistory } from 'react-router'
import { InventoryDisplay } from '../../../DashboardInventory/View/ActiveInventoryTable/InventoryDisplay'
import { CurrentUserStore } from '../../../../../stores/CurrentUserStore'
import { NoPermissionsResult } from '../../../../../components/NoPermissionsResult'
import { ColumnProps } from 'antd/lib/table'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
  currentUserStore?: CurrentUserStore
}

interface Props {
  product: CreateProduct | CreateChildProduct
}

interface InventoryTableRecord {
  key: number
  location: string
  quantity: JSX.Element
}

interface State {
  inventories: IInventoryWithLocationName[]
  isLoading: boolean
  visible: boolean
  product?: IBaseProduct
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class InventoryTab extends React.Component<Props & InjectedProps, State> {
  queryFilter: QueryFilter
  locations = this.props.currentCompanyStore?.locations || []
  company = this.props.currentCompanyStore!.company!

  constructor(props: Props) {
    super(props)
    this.queryFilter = new QueryFilter(browserHistory)
    this.state = {
      inventories: [],
      isLoading: false,
      visible: false,
      product: {} as IBaseProduct,
    }
    if (this.props.product) {
      const productIds = this.createProductIdArray(this.props.product)
      this.queryFilter.setProductIds(productIds)
    }
  }

  createProductIdArray = (product: CreateProduct | CreateChildProduct) => {
    const productIds = new Array<string>()
    if (product instanceof CreateProduct && product.childProducts.length > 0) {
      product.childProducts.forEach((child) => {
        if (child.id) {
          productIds.push(child.id)
        }
      })
    } else {
      if (product.id) {
        productIds.push(product.id)
      }
    }
    return productIds
  }

  async componentDidMount() {
    const allLocationIds = this.getLocationIds(this.locations)
    this.queryFilter.setSelectedLocationIds(allLocationIds)
    await this.getInventoryByProduct()
  }

  getLocationIds = (inventoryLocations: ILocation[]): number[] => {
    return inventoryLocations.map((location) => location.id)
  }

  getInventoryByProduct = async () => {
    try {
      if (this.props.product.id) {
        this.setState({ isLoading: true })

        const searchFilters = this.inventorySearchFilter
        const { multiLocationInventory } = await API.getInventoryByMultipleLocations(
          this.company.id,
          searchFilters,
        )
        if (this.queryFilter.inventoryFilter.selectedLocationIds.length) {
          this.setState({
            inventories: multiLocationInventory[0]?.inventories || [],
            product: multiLocationInventory[0].product,
          })
        } else {
          this.setState({ inventories: [] })
        }
      }
    } catch (error) {
      message.error(parseErrorMsg(error) || 'An error occurred while loading inventory logs')
    } finally {
      this.setState({ isLoading: false })
    }
  }

  get inventorySearchFilter(): InventorySearchFilter {
    const { selectedLocationIds } = this.queryFilter.inventoryFilter

    const locationIds = selectedLocationIds.length
      ? selectedLocationIds
      : this.getLocationIds(this.locations)

    // Create a new object by adding "hideEmptyQuantities"
    const inventoryFilterWithHideEmptyQuantities = {
      sku: undefined,
      locationIds,
      ...this.queryFilter.inventoryFilter,
      hideEmptyQuantities: false,
      offset: 0,
    }
    return new InventorySearchFilter(inventoryFilterWithHideEmptyQuantities)
  }

  handleLocationSelectAll = (all: boolean) => {
    if (all) {
      const locationIds = this.locations.map((location) => location.id)
      this.queryFilter.setSelectedLocationIds(locationIds)
    } else {
      this.queryFilter.setSelectedLocationIds([])
    }
  }

  handleVisibleChange = (visible: boolean) => {
    this.setState({ visible })
  }

  get dropdownOverlay(): JSX.Element {
    return (
      <Menu
        onClick={(e) => {
          this.handleVisibleChange(true)
          e.domEvent.stopPropagation()
        }}
      >
        {this.locations.length > 1 && (
          <Menu.Item key='all-none'>
            <div className='text-right w-100'>
              <a className='mr-2' onClick={() => this.handleLocationSelectAll(true)}>
                All
              </a>
              <a onClick={() => this.handleLocationSelectAll(false)}>None</a>
            </div>
          </Menu.Item>
        )}

        {this.locations.map((location) => {
          return (
            <Menu.Item key={location.id}>
              <Checkbox
                checked={this.queryFilter.inventoryFilter.selectedLocationIds.includes(location.id)}
                onClick={() => {
                  this.queryFilter.toggleSelectedLocationIds(location.id)
                }}
              >
                {location.name}
              </Checkbox>
            </Menu.Item>
          )
        })}
      </Menu>
    )
  }

  get tableDataSource(): InventoryTableRecord[] {
    const { product } = this.state
    if (product) {
      return this.state.inventories.map((inventory) => {
        return {
          key: inventory.locationId,
          location: inventory.locationName,
          quantity: this.renderQuantity(product, inventory),
          lowStockQuantity: inventory.lowStockQuantity,
        }
      })
    } else {
      return []
    }
  }

  renderQuantity = (product: IBaseProduct, inventory: IInventoryWithLocationName) => {
    return (
      <InventoryDisplay
        key={inventory.locationId}
        locationId={inventory.locationId}
        product={product}
        quantity={inventory.quantity}
        lowStockQuantity={inventory.lowStockQuantity}
        restockQuantity={inventory.restockQuantity}
        showLowStockQuantity={false}
        onUpdate={() => this.getInventoryByProduct()}
      />
    )
  }

  render() {
    const canViewInventoryAtAnyLocation = this.props.currentUserStore!.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_INVENTORY,
      this.company.id,
    )

    // If the user doesn't have view inventory permissions anywhere, show a warning message
    if (!canViewInventoryAtAnyLocation) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_INVENTORY} />
    }

    const columns: ColumnProps<InventoryTableRecord>[] = [
      {
        title: 'Location',
        key: 'location',
        dataIndex: 'location',
        width: '80%',
      },
      {
        title: 'Quantity',
        key: 'quantity',
        dataIndex: 'quantity',
        width: '20%',
        align: 'right' as const,
        render: (text) => {
          let className = 'inventory-row-quantity '
          const lowStockQuantity = text.props.lowStockQuantity
          if (lowStockQuantity) {
            if (text.props.quantity <= 0) {
              className += 'danger'
            } else if (text.props.quantity > 0 && text.props.quantity <= lowStockQuantity) {
              className += 'warning'
            } else {
              className += 'success'
            }
          } else {
            if (text.props.quantity > 0) {
              className += 'success'
            } else if (text.props.quantity < 0) {
              className += 'danger'
            }
          }
          return {
            props: {
              className: className,
            },
            children: text,
          }
        },
      },
    ]

    return (
      <div>
        <Dropdown
          getPopupContainer={getPopupContainer}
          trigger={['click']}
          overlay={this.dropdownOverlay}
          placement='bottomCenter'
          onVisibleChange={this.handleVisibleChange}
          visible={this.state.visible}
        >
          <Button>
            {this.queryFilter.inventoryFilter.selectedLocationIds.length ===
            this.locations.length ? (
              <span>Locations (all)</span>
            ) : (
              <span>Locations ({this.queryFilter.inventoryFilter.selectedLocationIds.length})</span>
            )}
            <Icon type='down' />
          </Button>
        </Dropdown>
        <Button type='primary' className='ml-2' onClick={this.getInventoryByProduct}>
          Apply
        </Button>
        <Table
          className='mt-3 background-white show-horizontal-scrollbar no-background-table'
          pagination={false}
          size='middle'
          dataSource={this.tableDataSource}
          columns={columns}
          rowKey={(inventories) => inventories.key.toString()}
          scroll={{ x: true, y: 310 }}
          loading={this.state.isLoading}
          locale={{
            emptyText: (
              <Empty
                description={
                  this.props.product.trackInventory ? 'No data' : 'Inventory tracking is disabled'
                }
              />
            ),
          }}
        />
      </div>
    )
  }
}
